import React, { useState, useEffect, useRef, FC, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { SubmitButton } from './SubmitButton';
import { DEFAULT_GRID, type Cell } from './EmotionIcons';
export type { Cell };

export type EmotionPickerValue = {
  id: string;
  icon?: React.FC<{ className?: string }>;
  label: string;
  valence: number; // -2..+2
  arousal: number; // -2..+2
  message?: string;
};

interface EmotionPickerProps {
  onSelect?: (v: EmotionPickerValue) => void;
  onSelectionChange?: (v: EmotionPickerValue | undefined) => void;
  onSubmitStart?: (v: EmotionPickerValue) => void;
}

const ROWS = [
  DEFAULT_GRID.slice(0, 4),
  DEFAULT_GRID.slice(4, 8),
  DEFAULT_GRID.slice(8, 12),
  DEFAULT_GRID.slice(12, 16),
];

const CELL_CLS_ICON =
  'tw-relative tw-flex tw-items-center tw-justify-center tw-font-atkinson tw-p-0 tw-cursor-pointer focus:!tw-shadow-none focus:!tw-outline-none focus:!tw-outline-offset-2';
const CELL_CLS_EMPTY =
  'tw-relative tw-flex tw-items-center tw-justify-center tw-font-atkinson tw-p-0 tw-cursor-default';

export const EmotionPicker: FC<EmotionPickerProps> = ({ onSelect, onSelectionChange, onSubmitStart }) => {
  const [selected, setSelected] = useState<EmotionPickerValue | null>(null);
  const [isLocking, setIsLocking] = useState(false);
  const [hoveredCell, setHoveredCell] = useState<string | null>(null);
  const [focusedCellId, setFocusedCellId] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const handleSelect = useCallback(
    (cell: Cell) => {
      if (!cell.icon || isLocking) return;
      const v: EmotionPickerValue = { ...cell };
      setSelected(v);
      onSelectionChange?.(v);
    },
    [isLocking, onSelectionChange],
  );

  // Keyboard navigation for grid
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, rowIdx: number, colIdx: number, _cellId: string) => {
      if (isLocking) return;

      let newRow = rowIdx;
      let newCol = colIdx;
      let shouldPreventDefault = true;

      switch (e.key) {
        case 'ArrowRight':
          newCol = Math.min(3, colIdx + 1);
          break;
        case 'ArrowLeft':
          newCol = Math.max(0, colIdx - 1);
          break;
        case 'ArrowDown':
          newRow = Math.min(3, rowIdx + 1);
          break;
        case 'ArrowUp':
          newRow = Math.max(0, rowIdx - 1);
          break;
        case 'Home':
          newCol = 0;
          break;
        case 'End':
          newCol = 3;
          break;
        default:
          shouldPreventDefault = false;
      }

      if (shouldPreventDefault) {
        e.preventDefault();
        const nextCell = gridRef.current?.querySelector<HTMLElement>(`[data-row="${newRow}"][data-col="${newCol}"]`);
        nextCell?.focus();
      }

      // Allow selection with Enter or Space
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const cell = DEFAULT_GRID[newRow * 4 + newCol];
        if (cell?.icon) {
          handleSelect(cell);
        }
      }
    },
    [isLocking, handleSelect],
  );

  const handleSubmit = useCallback(() => {
    if (!selected || isLocking) return;
    setIsLocking(true);
    setHoveredCell(null); // Clear hover tooltip on submit
    onSubmitStart?.(selected);
    timerRef.current = window.setTimeout(() => {
      onSelect?.(selected);
      setIsLocking(false);
      setSelected(null);
      onSelectionChange?.(undefined);
    }, 1500);
  }, [selected, isLocking, onSelect, onSelectionChange, onSubmitStart]);

  return (
    <div className={'tw-flex tw-items-center tw-gap-4 tw-select-none '}>
      <div
        ref={gridRef}
        role="grid"
        aria-label="Emotion circumplex"
        aria-rowcount={4}
        aria-colcount={4}
        className="tw-rounded-2xl tw-border !tw-p-0"
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '12px',
          borderColor: 'rgba(241, 240, 238, 1.00)',
        }}
      >
        {ROWS.map((row, rIdx) => (
          <div
            key={rIdx}
            role="row"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 48px)',
              gap: '6px',
            }}
          >
            {row.map((cell, cIdx) => {
              const isSelected = selected?.id === cell.id;
              const isHovered = hoveredCell === cell.id;
              const isFocused = focusedCellId === cell.id;
              const isFirstCell = rIdx === 0 && cIdx === 0;

              return (
                <div key={`${rIdx}-${cIdx}`} className="tw-relative">
                  <motion.button
                    role="gridcell"
                    aria-label={cell.label || cell.id}
                    aria-selected={isSelected}
                    aria-rowindex={rIdx + 1}
                    aria-colindex={cIdx + 1}
                    data-row={rIdx}
                    data-col={cIdx}
                    tabIndex={isFocused || (focusedCellId === null && isFirstCell) ? 0 : -1}
                    disabled={!cell.icon}
                    onClick={() => handleSelect(cell)}
                    onMouseEnter={() => cell.icon && setHoveredCell(cell.id)}
                    onMouseLeave={() => setHoveredCell(null)}
                    onFocus={() => {
                      setFocusedCellId(cell.id);
                      if (cell.icon) setHoveredCell(cell.id);
                    }}
                    onBlur={() => {
                      setFocusedCellId(null);
                      setHoveredCell(null);
                    }}
                    onKeyDown={e => handleKeyDown(e, rIdx, cIdx, cell.id)}
                    className={cell.icon ? CELL_CLS_ICON : CELL_CLS_EMPTY}
                    style={{
                      background: '#EBE4DC',
                      border: '0px solid rgb(0,0,0)',
                      width: '100%',
                      height: '100%',
                      color: isHovered || isFocused || isSelected ? '#5776FC' : undefined,
                      ['--icon-border' as string]: isHovered || isFocused || isSelected ? '#5776FC' : undefined,
                      transition: 'color 0.2s ease, --icon-border 0.2s ease',
                    }}
                    animate={{
                      scale: isSelected ? 0.95 : isHovered ? 1.08 : 1,
                    }}
                    transition={{
                      type: 'spring',
                      stiffness: 400,
                      damping: 25,
                    }}
                  >
                    {cell.icon && <cell.icon className="tw-w-12 tw-h-12" />}
                  </motion.button>

                  <AnimatePresence>
                    {isHovered && cell.icon && !isLocking && (
                      <div
                        className="tw-absolute tw-z-50 tw-pointer-events-none"
                        style={{
                          top: 'calc(-175%)',
                          left: '50%',
                          maxWidth: '384px',
                          width: '200px',
                          padding: '0px',
                        }}
                      >
                        <div
                          className="tw-bg-white tw-rounded-lg tw-font-atkinson tw-font-bold tw-text-base tw-text-indigo-600 tw-capitalize"
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            position: 'relative',
                            transform: 'translateX(-50%)',
                          }}
                        >
                          {cell.id}
                          <div
                            className="tw-absolute tw-w-2 tw-h-2 tw-bg-white"
                            style={{
                              bottom: '-4px',
                              left: '50%',
                              marginLeft: '-4px',
                              transform: 'rotate(45deg)',
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <SubmitButton onClick={handleSubmit} disabled={!selected || isLocking} ariaLabel="Submit emotion selection" />
      {/* Screen reader announcement for selected emotion */}
      <div className="tw-sr-only" aria-live="polite" aria-atomic="true">
        {selected ? `${selected.label} selected` : ''}
      </div>
    </div>
  );
};
